import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { addDays } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { noticesFor, useNotices } from '../data/courseStatus.ts'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { useAssets, type AssetRow } from '../data/fleet.ts'
import { useRpc } from '../data/people.ts'
import { settingLines, usePlan, warningsOf, weekOf, type AssignmentRow, type Plan, type PlanJob, type PlanOverview, type TaskTypeRow } from '../data/plan.ts'
import { areaShort, areasSummary, daysBetween, filterJobs, JOB_FILTERS, longDay, machineLabel, sortedAreaIds, timeShort, useNames, useRoster, type JobFilter, type RosterPerson } from '../data/planUi.ts'
import { outsideEmployment, type Proposal } from '../data/rotation.ts'
import { useAvailability } from '../data/safety.ts'
import { useMyWork } from '../data/work.ts'
import { localDate } from '../lib/time.ts'
import type { Pending } from '../sync/merge.ts'
import { Banner, errorText, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { LookStrip } from '../ui/looks/LookStrip.tsx'
import { SettingsWarningLine, useSettingsWarnings } from '../ui/SettingsWarning.tsx'
import type { SettingsWarning } from '../data/settingsRule.ts'
import { NoticeLine } from '../ui/NoticeLine.tsx'
import { Picto } from '../ui/pictos.tsx'
import { profileOf } from '../ui/profile.ts'
import { AssignSheet } from './plan/AssignSheet.tsx'
import { SettingLinesView, frameOfAreas } from './plan/JobSettings.tsx'
import { RotationPanel } from './plan/RotationPanel.tsx'
import { useReadsWork } from './plan/useSettingsData.ts'
import { groupName, initials, weekWindow } from './plan/shared.ts'
import { WeekGrid } from './plan/WeekGrid.tsx'

const ISO = /^\d{4}-\d{2}-\d{2}$/
export { groupName }

/** `sm:` and up (M23 item 26): seven columns and the whole toolbar; below it a phone, a three-day window and the toolbar behind "Plus". A test without
 *  `matchMedia` is wide. */
const WIDE = '(min-width: 640px)'
const subscribeWide = (cb: () => void) => {
  const m = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(WIDE) : null
  if (!m) return () => {}
  m.addEventListener('change', cb)
  return () => m.removeEventListener('change', cb)
}
const readWide = () => (typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia(WIDE).matches)
export const useWide = (): boolean => useSyncExternalStore(subscribeWide, readWide, () => true)

/** The day or week board (GK-014): who does what, on which machine, starting where, with the problems the server sees in the plan. Everybody reads it
 *  from their own device (offline included); the head greenkeeper plans from it. First jobs and second jobs are groups, not hours. M23: the jardinier's
 *  day comes first for the field profile (items 16, 21), the planner's board is compact cards with a filter (item 27), the week fits a phone (item 26). */
export function PlanBoardPage() {
  const { t, i18n } = useTranslation()
  const { org, site, personId, kiosk } = useClub()
  const planner = useWriteAccess('job.plan')
  const exec = useWriteAccess('job.execute')
  const availabilityEdit = useWriteAccess('availability.manage')
  const tplAccess = useWriteAccess('template.publish'); const recAccess = useWriteAccess('recurrence.publish'); const procAccess = useWriteAccess('procedure.publish')
  const setupLink = tplAccess.permitted || recAccess.permitted || procAccess.permitted
  const search = useSearch({ strict: false }) as { d?: string; v?: string }
  const navigate = useNavigate()
  const lang = i18n.language
  const wide = useWide()
  const today = localDate(new Date(), site.timezone)
  const date = search.d && ISO.test(search.d) ? search.d : today
  const view = search.v === 'week' ? 'week' : 'day'
  const me = kiosk?.session?.personId ?? personId ?? null
  const field = profileOf(site.roles, !!kiosk) === 'field'
  const week = useMemo(() => weekOf(date), [date])
  /** the days the grid shows: the week, or three days around the day chosen on a phone (the week's data stays the seven days) */
  const shownDays = useMemo(() => (view === 'week' ? weekWindow(week, date, wide, addDays) : [date]), [view, week, date, wide])
  /** every day the page reads (the week and the window may straddle: a Monday's window starts on Sunday) */
  const days = useMemo(() => {
    if (view === 'day') return [date]
    const all = [...week, ...shownDays].sort()
    return daysBetween(all[0]!, all[all.length - 1]!, addDays)
  }, [view, week, shownDays, date])
  const { ready, plan, taskTypes } = usePlan(lang)
  // M20 review: the people of the days shown (whoever worked them, and whoever still holds a line on them after leaving); the names of everybody, departed people included
  const shown = useMemo(() => ({ from: days[0]!, to: days[days.length - 1]!, holding: new Set(days.flatMap((d) => plan.day(d).flatMap((j) => j.crew.map((a) => a.person_id)))) }), [days, plan])
  // M25-D: the sand line of a card whose planned direction or pattern repeats the previous cut (only where the viewer holds job.plan and worklog.read: rule 2)
  const warnJobs = useMemo(() => days.flatMap((d) => plan.day(d)), [days, plan])
  const settingsWarn = useSettingsWarnings(warnJobs)
  const readsWork = useReadsWork()
  const roster = useRoster(shown); const names = useNames(); const areas = useAreas(); const assets = useAssets(); const av = useAvailability()
  // M16-B1 (placed by Z): the course notices in force on the board's day, one line each over the day board (the week grid shows its own, per F)
  const notices = useNotices()
  const dayNotices = useMemo(() => noticesFor(notices, date), [notices, date])
  const work = useMyWork(plan.jobs)
  const overview = useRpc<PlanOverview>('plan_overview', { p_org: org.org_id, p_site: site.site_id, p_from: days[0]!, p_to: days[days.length - 1]! }, planner.permitted)
  const [panel, setPanel] = useState<'copy' | 'away' | null>(null)
  const [more, setMore] = useState(false)
  const [rev, setRev] = useState(0)                                   // bumped by a change made on this page (an absence), so the rotation advice is read again
  const go = (d: string, v: 'day' | 'week' = view) => void navigate({ to: '/plan', search: { d, v } as never })
  const step = view === 'week' && wide ? 7 : 1

  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  const assetById = useMemo(() => new Map((assets ?? []).map((a) => [a.id, a as AssetRow])), [assets])
  const typeByCode = useMemo(() => new Map(taskTypes.map((x) => [x.code, x])), [taskTypes])
  const openOn = useMemo(() => new Map(work.mine.filter((w) => w.session.job_id).map((w) => [w.session.job_id!, w.session.id])), [work.mine])
  if (!ready || !roster || !names || !areas || !assets) return <p>{t('common.loading')}</p>
  const ctx: Ctx = { lang, planner: planner.allowed, overview: overview.data, areaById, assetById, typeByCode, blocked: av.isBlocked, roster, names, me, field, exec: exec.allowed, openOn, today, settingsWarn }
  const office = `btn !min-h-11 !px-4`

  return (
    <section className="flex flex-col gap-4" data-testid="plan-board">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">{t('plan.title')}</h1>
        <div className="flex flex-wrap gap-2">
          {planner.allowed && <Link to="/plan/new" search={{ d: date }} className="btn btn-primary !min-h-11 !px-4" data-testid="plan-new-job">{t('plan.newJob')}</Link>}
          <button className={`${office} sm:hidden`} aria-expanded={more} onClick={() => setMore(!more)} data-testid="plan-more">{t('plan.more')}</button>
        </div>
      </div>
      {/* M23 item 26: on a phone the rest of the toolbar waits behind "Plus"; from sm: it is all there */}
      <div className={`${more ? 'flex' : 'hidden'} flex-wrap gap-2 sm:flex`} data-testid="plan-tools">
        <button className={office} onClick={() => go(today)} disabled={date === today && view === 'day'} data-testid="plan-today">{t('plan.today')}</button>
        {/* item 16: the sheet and the shed screen are the planner's; on a wide screen everybody keeps them */}
        <Link to="/plan/print" search={{ d: date }} className={`${office} ${planner.permitted ? '' : 'hidden sm:inline-flex'}`} data-testid="plan-print">{t('plan.print')}</Link>
        <Link to="/plan/display" className={`${office} ${planner.permitted ? '' : 'hidden sm:inline-flex'}`} data-testid="plan-display">{t('plan.display')}</Link>
        {planner.allowed && <button className={office} aria-expanded={panel === 'copy'} onClick={() => setPanel(panel === 'copy' ? null : 'copy')} data-testid="plan-copy-open">{t('plan.copyDay')}</button>}
        {planner.allowed && availabilityEdit.allowed && <button className={office} aria-expanded={panel === 'away'} onClick={() => setPanel(panel === 'away' ? null : 'away')} data-testid="plan-away-open">{t('plan.away')}</button>}
        {planner.allowed && setupLink && <Link to="/plan/setup" className={office} data-testid="plan-setup">{t('plan.setup')}</Link>}
        {/* M16-C: the pins' history, exclusions and sheet (everybody may read the page; its forms are flag.manage's) */}
        <Link to="/plan/pins" search={{ d: date }} className={office} data-testid="plan-pins">{t('plan.pins')}</Link>
        {/* M25-D: what was done and what is planned, per series (the direction's: it shows other people's finished work) */}
        {readsWork && <Link to="/plan/settings" search={{ d: date }} className={office} data-testid="plan-settings-track">{t('plan.settingsTrack.title')}</Link>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button className={`${office} !min-w-14`} onClick={() => go(addDays(date, -step))} aria-label={t('plan.prev')} data-testid="plan-prev">‹</button>
        <input type="date" className="input !min-h-11 !w-auto" value={date} onChange={(e) => ISO.test(e.target.value) && go(e.target.value)} aria-label={t('plan.date')} data-testid="plan-date" />
        <button className={`${office} !min-w-14`} onClick={() => go(addDays(date, step))} aria-label={t('plan.next')} data-testid="plan-next">›</button>
        <div className="ml-auto flex gap-1" role="group" aria-label={t('plan.view')}>
          {(['day', 'week'] as const).map((v) => <button key={v} className={`${office} ${view === v ? 'btn-primary' : ''}`} aria-pressed={view === v} onClick={() => go(date, v)} data-testid={`plan-view-${v}`}>{t(`plan.view_${v}`)}</button>)}
        </div>
      </div>
      <p className="text-lg font-semibold first-letter:uppercase" data-testid="plan-heading">{view === 'week' ? t('plan.weekOf', { day: longDay(week[0]!, lang) }) : longDay(date, lang)}</p>
      {view === 'day' && dayNotices.length > 0 && <div className="flex flex-col gap-1" data-testid="board-notices">{dayNotices.map((n) => <NoticeLine key={n.id} notice={n} areaById={areaById} lang={lang} />)}</div>}
      {planner.readOnlyMode && <Banner tone="warn">{t('common.onlineOnly')}</Banner>}
      {panel === 'copy' && <CopyDay from={date} onDone={(to) => { setPanel(null); go(to, 'day') }} />}
      {panel === 'away' && <AwayPanel roster={roster} start={date} onDone={() => { void overview.reload(); setRev((n) => n + 1) }} />}
      {planner.permitted && overview.data && <WarningsBanner overview={overview.data} plan={plan} ctx={ctx} />}
      {view === 'day' ? <DayView date={date} plan={plan} ctx={ctx} />
        : <WeekBoard week={week} days={shownDays} date={date} today={today} plan={plan} ctx={ctx} assets={assets as AssetRow[]} reloadOverview={overview.reload} rev={rev} onDay={(d) => go(d, 'day')} />}
    </section>
  )
}

export interface Ctx {
  lang: string; planner: boolean; overview: PlanOverview | null; areaById: Map<string, AreaRow>; assetById: Map<string, AssetRow>; typeByCode: Map<string, TaskTypeRow>
  blocked: (assetId: string) => boolean; roster: RosterPerson[]
  /** every person's name (a departed person's lines still say who) */
  names: Map<string, string>
  /** M23 items 16, 21: the person looking at the board (the unlocked person on a workstation), whether they get the jardinier's screens, whether they may start work */
  me: string | null; field: boolean; exec: boolean
  /** the person's open timers by job id, for "Ouvrir le chrono" (the same card as Today) */
  openOn: Map<string, string>
  today: string
  /** M25-D: per job id, the planned direction or pattern that repeats the previous cut (empty where the viewer may not read the history) */
  settingsWarn?: ReadonlyMap<string, SettingsWarning[]>
}

function WarningsBanner({ overview, plan, ctx }: { overview: PlanOverview; plan: Plan; ctx: Ctx }) {
  const { t } = useTranslation()
  if (overview.warnings.length === 0) return null
  return (
    <Banner tone="warn">
      <details open={overview.warnings.length <= 3} data-testid="plan-warnings">
        <summary className="min-h-14 cursor-pointer content-center font-semibold">{t('plan.warnings', { count: overview.warnings.length })}</summary>
        <ul className="mt-1 flex flex-col">
          {overview.warnings.map((w, i) => {
            const job = w.job_id ? plan.byId.get(w.job_id) : undefined
            const who = w.person_id ? ctx.names.get(w.person_id) : undefined
            const text = `${who ? `${who} · ` : ''}${errorText(t, w.code)}`
            // M23 item 27: each warning is a 44-px row that is the link to its job (they were 17-px inline links)
            return (
              <li key={i} data-testid="plan-warning" data-code={w.code}>
                {job ? <Link to="/plan/job/$jobId" params={{ jobId: job.id }} className="flex min-h-11 items-center gap-1 underline-offset-2 hover:underline"><span>{text} — <span className="font-semibold">{job.title_text}</span></span></Link>
                  : <span className="flex min-h-11 items-center">{text}</span>}
              </li>
            )
          })}
        </ul>
      </details>
    </Banner>
  )
}

/** The Start button of the Today card (M23 items 16, 18, 21): "Ouvrir le chrono" when a timer of mine is open on the job, else "Commencer" (the short form of the one verb of the start path) when the job is not done
 *  and I may execute; nothing for somebody who cannot. */
function StartButton({ job, line, ctx }: { job: PlanJob; line?: Pending<AssignmentRow>; ctx: Ctx }) {
  const { t } = useTranslation()
  const session = ctx.openOn.get(job.id)
  if (session) return <Link to="/work/$sessionId" params={{ sessionId: session }} className="btn" data-testid="plan-open-timer">{t('work.openTimer')}</Link>
  if (job.state === 'done' || job.state === 'cancelled' || !ctx.exec) return null
  return <Link to="/work/start" search={{ job: job.id, ...(line?.asset_id ? { asset: line.asset_id } : {}) } as never} className="btn btn-primary" data-testid="plan-start">{t('work.start')}</Link>
}

/** One job as a compact card (M23 item 27): one line of title and time, one line of who · machines · zones · points of attention, open on tap for the whole
 *  of it (the zones, the settings, the crew lines, the warnings, the link to the job's page). The open state is the page's, per job. For the field profile's own
 *  jobs the card opens with its Start button (item 16). */
function JobCard({ job, ctx, open, onToggle, line, start }: { job: PlanJob; ctx: Ctx; open: boolean; onToggle(open: boolean): void; line?: Pending<AssignmentRow>; start?: boolean }) {
  const { t } = useTranslation()
  const tt = ctx.typeByCode.get(job.task_type_code)
  const warns = warningsOf(ctx.overview, job.id)
  const need = job.required_crew
  const lines = settingLines(job.params, tt, ctx.lang)
  const frame = frameOfAreas(job.areaIds, ctx.areaById)
  const settingsWarns = ctx.settingsWarn?.get(job.id) ?? []
  const who = job.crew.map((a) => initials(ctx.names.get(a.person_id) ?? '?')).join(' ')
  const tags = job.crew.flatMap((a) => (a.asset_id ? [ctx.assetById.get(a.asset_id)?.readable_tag ?? '?'] : []))
  const zones = job.areaIds.length > 0 ? areasSummary(job.areaIds, ctx.areaById, ctx.lang, t) : ''
  const meta = [who || t('plan.nobody'), tags.join(' '), zones, warns.length > 0 ? t('plan.warningCount', { count: warns.length }) : ''].filter(Boolean).join(' · ')
  return (
    <li className="card !p-0" data-testid="plan-job" data-job={job.id} data-state={job.state} data-open={open || undefined}>
      <details open={open} onToggle={(e) => onToggle(e.currentTarget.open)}>
        <summary className="flex min-h-14 cursor-pointer list-none items-center gap-2 px-3 py-1 [&::-webkit-details-marker]:hidden" data-testid="plan-job-summary">
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex items-baseline gap-2">
              <span className="line-clamp-2 font-semibold" data-testid="plan-job-name">{job.title_text}</span>
              {job.scheduled_start_local && <span className="shrink-0 text-sm font-semibold">{timeShort(job.scheduled_start_local)}</span>}
              <LookStrip lines={lines} frame={frame} className="shrink-0 self-center text-xl text-brand dark:text-fairway" testId="plan-job-looks" />
              {job.state !== 'planned' && <Pill tone={job.state === 'done' ? 'green' : 'amber'}>{t(`plan.state.${job.state}`)}</Pill>}
            </span>
            <span className={`meta truncate ${warns.length > 0 ? 'text-amber-800 dark:text-amber-300' : ''}`} data-testid="plan-job-meta">{meta}</span>
          </span>
          <Picto name="chevron" className={`size-5 shrink-0 transition-transform ${open ? '-rotate-90' : 'rotate-90'}`} strokeWidth={2.2} />
        </summary>
        <div className="flex flex-col gap-2 border-t border-slate-200 px-3 pb-3 pt-2 dark:border-night-line">
          <div className="flex flex-wrap items-center gap-1.5">
            {job.origin === 'recurrence' && <Pill tone="gray">↻ {t('plan.recurring')}</Pill>}
            {need !== null && <Pill tone={job.crew.length < need ? 'amber' : 'green'}>{t('plan.crewCount', { have: job.crew.length, need })}</Pill>}
            {sortedAreaIds(job.areaIds, ctx.areaById, ctx.lang).map((id) => <span key={id} className="rounded-md bg-slate-100 px-2 py-0.5 text-sm dark:bg-slate-700" data-testid="plan-job-area">{areaShort(ctx.areaById.get(id), ctx.areaById, ctx.lang, t)}</span>)}
          </div>
          {lines.length > 0 && <SettingLinesView lines={lines} frame={frame} testId="plan-job-params" className="text-sm" />}
          {job.notes && <p className="text-sm italic">{job.notes}</p>}
          {job.crew.length === 0 ? <p className="meta" data-testid="plan-job-nobody">{t('plan.nobody')}</p> : (
            <ul className="flex flex-col gap-1" data-testid="plan-crew">
              {job.crew.map((a) => {
                const asset = a.asset_id ? ctx.assetById.get(a.asset_id) : undefined
                const startArea = a.start_area_id ? ctx.areaById.get(a.start_area_id) : undefined
                return (
                  <li key={a.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm" data-testid="plan-crew-line">
                    <span className="font-semibold">{ctx.names.get(a.person_id) ?? '?'}</span>
                    {asset && <span>{machineLabel(asset)}</span>}
                    {asset && ctx.blocked(asset.id) && <Pill tone="red">{t('plan.out')}</Pill>}
                    {startArea && <span className="text-slate-700 dark:text-slate-300">→ {areaShort(startArea, ctx.areaById, ctx.lang, t)}</span>}
                  </li>
                )
              })}
            </ul>
          )}
          {warns.length > 0 && <ul className="flex flex-col gap-0.5 text-sm text-amber-800 dark:text-amber-300" data-testid="plan-job-warnings">{warns.map((w, i) => <li key={i} data-code={w.code}>⚠ {w.person_id ? `${ctx.names.get(w.person_id) ?? ''} · ` : ''}{errorText(t, w.code)}</li>)}</ul>}
          <div className="flex flex-wrap gap-2">
            <Link to="/plan/job/$jobId" params={{ jobId: job.id }} className="btn" data-testid="plan-job-title">{t('plan.openJob')}</Link>
            {start && <StartButton job={job} line={line} ctx={ctx} />}
          </div>
        </div>
      </details>
      {settingsWarns.length > 0 && <SettingsWarningLine warnings={settingsWarns} className="mx-3 mb-2" />}
    </li>
  )
}

/** The day. For the field profile (M23 item 16): "Mes tâches" first, open, with the Start button, then the whole team folded; for the planner the compact
 *  cards with a filter (item 27) and the people without a job (planners only). */
export function DayView({ date, plan, ctx }: { date: string; plan: Plan; ctx: Ctx }) {
  const { t } = useTranslation()
  const jobs = plan.day(date)
  const mine = ctx.me ? plan.ofPerson(ctx.me, date) : []
  const [filter, setFilter] = useState<JobFilter>('all')
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(ctx.field ? mine.map((m) => m.job.id) : []))     // the jardinier's own cards open; the planner's board starts closed
  // another day: the filter and the open cards start again (review of item 27: a filter chosen on Monday hid Tuesday's board); the jardinier's own
  // cards also open when his jobs arrive after the mount (a device whose first sync was still pulling)
  const mineIds = mine.map((m) => m.job.id).join(',')
  useEffect(() => { setFilter('all'); setOpen(new Set(ctx.field && mineIds ? mineIds.split(',') : [])) }, [date])             // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (ctx.field && mineIds) setOpen((cur) => new Set([...cur, ...mineIds.split(',')])) }, [ctx.field, mineIds])
  const toggle = (id: string, o: boolean) => setOpen((cur) => { const next = new Set(cur); if (o) next.add(id); else next.delete(id); return next })
  const shown = ctx.field ? jobs : filterJobs(jobs, filter, { me: ctx.me, warnings: ctx.overview?.warnings })
  const groups = [...new Set(shown.map((j) => j.sequence_group))].sort((a, b) => a - b)
  const busy = new Set(jobs.flatMap((j) => j.crew.map((a) => a.person_id)))
  const away = new Map((ctx.overview?.availability ?? []).filter((a) => a.local_date === date).map((a) => [a.person_id, a.status]))
  const team = ctx.roster.filter((p) => !outsideEmployment(date, p.employedFrom, p.employedUntil))      // M20: not before their first day, not after their last
  const free = team.filter((p) => !busy.has(p.id) && !away.has(p.id))
  const gone = team.filter((p) => !busy.has(p.id) && away.has(p.id))
  const board = (
    <>
      {jobs.length === 0 && <p className="opacity-70" data-testid="plan-empty">{t('plan.empty')}</p>}
      {jobs.length > 0 && shown.length === 0 && <p className="opacity-70" data-testid="plan-filter-empty">{t('plan.filterEmpty')}</p>}
      {groups.map((g) => (
        <section key={g} className="flex flex-col gap-2" data-testid="plan-group" data-group={g}>
          <h2 className="text-lg font-bold">{groupName(t, g)}</h2>
          <ul className="flex flex-col gap-1.5">{shown.filter((j) => j.sequence_group === g).map((j) => <JobCard key={j.id} job={j} ctx={ctx} open={open.has(j.id)} onToggle={(o) => toggle(j.id, o)} />)}</ul>
        </section>
      ))}
      {ctx.planner && (
        <section className="flex flex-col gap-1" data-testid="plan-unassigned">
          <h2 className="text-lg font-bold">{t('plan.unassigned')}</h2>
          {free.length === 0 ? <p className="opacity-70">{t('plan.noneFree')}</p> : <p>{free.map((p) => p.name).join(', ')}</p>}
          {gone.length > 0 && <p className="meta" data-testid="plan-away">{t('plan.awayList')}: {gone.map((p) => `${p.name} (${t(`plan.avail.${away.get(p.id)!}`)})`).join(', ')}</p>}
        </section>
      )}
    </>
  )
  return (
    <div className="flex flex-col gap-4" data-testid="plan-day">
      {ctx.field ? (
        <>
          <section className="flex flex-col gap-2" data-testid="plan-mine">
            <h2 className="text-lg font-bold">{t('plan.mine')}</h2>
            {mine.length === 0 ? <p className="opacity-70" data-testid="plan-mine-empty">{t('plan.mineNone')}</p>
              : <ul className="flex flex-col gap-2">{mine.map(({ job, assignment }) => <JobCard key={assignment.id} job={job} ctx={ctx} open={open.has(job.id)} onToggle={(o) => toggle(job.id, o)} line={assignment} start />)}</ul>}
          </section>
          <details className="flex flex-col gap-4" data-testid="plan-team">
            <summary className="btn w-full justify-between">{t('plan.team', { count: jobs.length })}<Picto name="chevron" className="size-5 rotate-90" strokeWidth={2.2} /></summary>
            <div className="mt-4 flex flex-col gap-4">{board}</div>
          </details>
        </>
      ) : (
        <>
          {/* two rows of two on a phone (four 56-px buttons do not fit 375 px side by side, and a row that scrolled sideways hid "Les miens" with no cue: review of item 27) */}
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" role="group" aria-label={t('plan.filterLabel')} data-testid="plan-filter">
            {JOB_FILTERS.filter((f) => f !== 'mine' || ctx.me).map((f) => (
              <button key={f} className={`btn !px-3 ${filter === f ? 'btn-primary' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)} data-testid={`plan-filter-${f}`}>{t(`plan.filter.${f}`)}</button>
            ))}
          </div>
          {board}
        </>
      )}
    </div>
  )
}

/** The week (M18): the grid of people and days, with what the head greenkeeper needs to fill it: "Envoyer quelqu'un" (Travail, Personne, Machine) from the toolbar,
 *  a cell or the "to staff" row, and the rotation assistant, whose lines show in the grid as dashed chips until they are applied. M23: the grid shows `days` (three on a
 *  phone, item 26) while the sheet and the assistant work on `week`; for the field profile "Ma semaine" comes first and the grid is folded (item 21). */
export function WeekBoard({ week, days, date, today, plan, ctx, assets, reloadOverview, rev, onDay }: {
  week: string[]; days: string[]; date: string; today: string; plan: Plan; ctx: Ctx; assets: AssetRow[]; reloadOverview: () => Promise<void>; rev: number; onDay: (d: string) => void
}) {
  const { t } = useTranslation()
  const assign = useWriteAccess('assignment.manage')
  const [by, setBy] = useState<'people' | 'machines'>('people')
  const [sheet, setSheet] = useState<{ day: string; personId?: string; key: number } | null>(null)
  const [rotation, setRotation] = useState(false)
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [sent, setSent] = useState<{ text: string; week: string } | null>(null)
  const [refresh, setRefresh] = useState(0)
  const open = useCallback((day: string, personId?: string) => { setSent(null); setSheet({ day, personId, key: Date.now() }) }, [])
  const close = useCallback(() => setSheet(null), [])
  const firstDay = days.includes(date) ? date : days.includes(today) ? today : days[0]!
  const grid = (
    <div className="flex flex-col gap-3" data-testid="plan-week-wrap">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1" role="group" aria-label={t('plan.rows')}>
          {(['people', 'machines'] as const).map((b) => <button key={b} className={`btn !min-h-11 !px-4 ${by === b ? 'btn-primary' : ''}`} aria-pressed={by === b} onClick={() => setBy(b)} data-testid={`plan-by-${b}`}>{t(`plan.by_${b}`)}</button>)}
        </div>
        {assign.allowed && (
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <button className="btn !min-h-11 !px-4" onClick={() => open(firstDay)} data-testid="plan-assign-open"><Picto name="people" className="size-5" />{t('plan.assign.open')}</button>
            <button className="btn !min-h-11 !px-4" aria-expanded={rotation} onClick={() => { setRotation(!rotation); if (rotation) setProposals([]) }} data-testid="plan-rotation-open"><Picto name="sync" className="size-5" />{t('plan.rotation.open')}</button>
          </div>
        )}
      </div>
      {sent && sent.week === week[0] && <Banner tone="ok"><span data-testid="assign-ok">{sent.text}</span></Banner>}
      {rotation && assign.allowed && (
        <RotationPanel days={week} plan={plan} lang={ctx.lang} refresh={refresh + rev} onProposals={setProposals}
          onApplied={() => void reloadOverview()} onClose={() => { setRotation(false); setProposals([]) }} />
      )}
      <WeekGrid days={days} today={today} lang={ctx.lang} by={by} plan={plan} roster={ctx.roster} assetById={ctx.assetById} overview={ctx.overview} blocked={ctx.blocked}
        canAssign={assign.allowed} proposals={rotation ? proposals : []} me={ctx.me} typeByCode={ctx.typeByCode} onDay={onDay} onAdd={(d, p) => open(d, p)} onOpenDay={(d) => open(d)} />
      {sheet && (
        <AssignSheet key={sheet.key} days={week} initialDay={sheet.day} personId={sheet.personId} plan={plan} roster={ctx.roster} assets={assets} areaById={ctx.areaById}
          overview={ctx.overview} blocked={ctx.blocked} lang={ctx.lang} onClose={close}
          onSaved={(message) => { setSheet(null); setSent({ text: message, week: week[0]! }); setRefresh((n) => n + 1); void reloadOverview() }} />
      )}
    </div>
  )
  if (!ctx.field) return grid
  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-2" data-testid="plan-my-week">
        <h2 className="text-lg font-bold">{t('plan.myWeek')}</h2>
        <ul className="flex flex-col gap-2">
          {week.map((d) => {
            const lines = ctx.me ? plan.ofPerson(ctx.me, d) : []
            return (
              <li key={d} className={`card flex flex-col gap-2 ${d === today ? 'border-brand dark:border-fairway' : ''}`} data-testid="plan-my-day" data-day={d} data-today={d === today || undefined}>
                <h3 className="font-semibold first-letter:uppercase">{longDay(d, ctx.lang)}</h3>
                {lines.length === 0 ? <p className="meta">{t('plan.mineNoneDay')}</p> : lines.map(({ job, assignment }) => {
                  const asset = assignment.asset_id ? ctx.assetById.get(assignment.asset_id) : undefined
                  return (
                    <div key={assignment.id} className="flex flex-wrap items-center justify-between gap-2" data-testid="plan-my-line" data-job={job.id}>
                      <Link to="/plan/job/$jobId" params={{ jobId: job.id }} className="flex min-h-14 min-w-0 flex-1 flex-col justify-center">
                        <span className="font-semibold">{job.title_text}{job.scheduled_start_local ? ` · ${timeShort(job.scheduled_start_local)}` : ''}</span>
                        {asset && <span className="meta">{machineLabel(asset)}</span>}
                      </Link>
                      {d === today && <StartButton job={job} line={assignment} ctx={ctx} />}
                    </div>
                  )
                })}
              </li>
            )
          })}
        </ul>
      </section>
      <details className="flex flex-col" data-testid="plan-team-week">
        <summary className="btn w-full justify-between">{t('plan.teamWeek')}<Picto name="chevron" className="size-5 rotate-90" strokeWidth={2.2} /></summary>
        <div className="mt-4">{grid}</div>
      </details>
    </div>
  )
}

function CopyDay({ from, onDone }: { from: string; onDone: (to: string) => void }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const [to, setTo] = useState(addDays(from, 1))
  const [crew, setCrew] = useState(true)
  return (
    <form className="card flex flex-col gap-3" data-testid="plan-copy" onSubmit={(e) => { e.preventDefault(); void w.run('job.copy_day@1', { from_date: from, to_date: to, with_crew: crew }, 'plan.copied').then((ok) => ok && onDone(to)) }}>
      <p className="text-sm">{t('plan.copyHelp', { from: from.split('-').reverse().join('/') })}</p>
      <Field label={t('plan.copyTo')} htmlFor="copy-to"><input id="copy-to" type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} data-testid="copy-to" /></Field>
      <label className="flex min-h-11 items-center gap-2"><input type="checkbox" className="size-5" checked={crew} onChange={(e) => setCrew(e.target.checked)} data-testid="copy-crew" />{t('plan.copyCrew')}</label>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error"><span data-testid="copy-error">{w.error}</span></Banner>}
      <button className="btn btn-primary self-start" disabled={w.busy || !ISO.test(to) || to === from} data-testid="copy-go">{t('plan.copyGo')}</button>
    </form>
  )
}

/** Availability is a status for a person and some days: working, partly, away. Never a reason. */
function AwayPanel({ roster, start, onDone }: { roster: RosterPerson[]; start: string; onDone: () => void }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const [person, setPerson] = useState(roster[0]?.id ?? '')
  const [from, setFrom] = useState(start); const [to, setTo] = useState(start)
  const [status, setStatus] = useState<'off' | 'partial' | 'working'>('off')
  const dates = ISO.test(from) && ISO.test(to) && to >= from ? daysBetween(from, to, addDays) : []
  return (
    <form className="card flex flex-col gap-3" data-testid="plan-away-form" onSubmit={(e) => { e.preventDefault(); void w.run('availability.set@1', { person_id: person, dates, status }, 'plan.awaySaved').then((ok) => ok && onDone()) }}>
      <p className="text-sm">{t('plan.awayHelp')}</p>
      <Field label={t('plan.person')} htmlFor="away-person">
        <select id="away-person" className="input" value={person} onChange={(e) => setPerson(e.target.value)} data-testid="away-person">{roster.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('plan.from')} htmlFor="away-from"><input id="away-from" type="date" className="input" value={from} onChange={(e) => { setFrom(e.target.value); if (e.target.value > to) setTo(e.target.value) }} data-testid="away-from" /></Field>
        <Field label={t('plan.to')} htmlFor="away-to"><input id="away-to" type="date" className="input" value={to} min={from} onChange={(e) => setTo(e.target.value)} data-testid="away-to" /></Field>
      </div>
      <Field label={t('plan.status')} htmlFor="away-status">
        <select id="away-status" className="input" value={status} onChange={(e) => setStatus(e.target.value as typeof status)} data-testid="away-status">
          {(['off', 'partial', 'working'] as const).map((s) => <option key={s} value={s}>{t(`plan.avail.${s}`)}</option>)}
        </select>
      </Field>
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error"><span data-testid="away-error">{w.error}</span></Banner>}
      {w.ok && <Banner tone="ok"><span data-testid="away-ok">{w.ok}</span></Banner>}
      <button className="btn btn-primary self-start" disabled={w.busy || !person || dates.length === 0} data-testid="away-go">{t('plan.awayGo', { count: dates.length })}</button>
    </form>
  )
}
